import React, { useState } from 'react';
import { Container, Row, Col, Card, Accordion, Modal } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { FEATURES } from '../../config/features';
import {
  UserPlus as FaUserPlus, Link2 as FaLink, CalendarCheck as FaCalendarCheck, Layers as FaLayerGroup,
  CircleCheck as FaCheckCircle, Search as FaSearch, Unlink as FaUnlink,
  Info as FaInfoCircle, Users as FaUsers
} from 'lucide-react';

const SECTIONS = [
  {
    key: 'add-manual',
    icon: <FaUserPlus size={20} />,
    title: 'Dodawanie pracownika ręcznie',
    content: (
      <>
        <p>
          Przejdź do zakładki <strong>„Pracownicy”</strong> i kliknij{' '}
          <strong>„Dodaj pracownika”</strong>. Ta opcja służy do ewidencjonowania
          pracowników, którzy <u>nie mają</u> własnego konta w systemie — dane
          (imię, nazwisko, data urodzenia, zawód) wpisujesz samodzielnie.
        </p>
        <ul className="mb-0">
          {FEATURES.youth && <li>Wybierz typ: <em>uczeń / młodociany</em> lub <em>pracownik dorosły</em>.</li>}
          <li>Wpisz roczną pulę urlopową (liczbę przyznanych dni).</li>
          <li>Dane i urlopy tego pracownika edytujesz w każdej chwili z poziomu panelu.</li>
        </ul>
      </>
    ),
  },
  {
    key: 'link-account',
    icon: <FaLink size={20} />,
    title: 'Zapraszanie / łączenie konta pracownika',
    content: (
      <>
        <p>
          Jeśli pracownik ma <strong>własne konto</strong> w systemie, przejdź do{' '}
          <strong>„Połącz konto”</strong>. Wyszukaj go po pełnym adresie email
          (min. 3 znaki, wpisując poprawny email pojawi się wynik) i kliknij{' '}
          <strong>„Zaproś”</strong> <FaSearch size={12} className="mx-1" />.
        </p>
        <ul className="mb-2">
          <li>Zaproszenie trafia do pracownika ze statusem <em>„Oczekuje”</em>.</li>
          <li>Pracownik akceptuje lub odrzuca je w swoim panelu.</li>
          <li>Po akceptacji pracownik pojawia się na liście jako <em>„Połączone konto”</em>{' '}
            <FaLink size={10} />.</li>
          <li>Pracownik może być połączony tylko z <strong>jednym</strong> pracodawcą naraz.</li>
        </ul>
        <p className="mb-0 d-flex align-items-center gap-2">
          <FaUnlink /> W każdej chwili możesz zakończyć połączenie przyciskiem{' '}
          <strong>„Rozłącz”</strong> — pracownik straci dostęp do składania wniosków,
          ale może połączyć się ponownie w przyszłości.
        </p>
      </>
    ),
  },
  {
    key: 'pool',
    icon: <FaCalendarCheck size={20} />,
    title: 'Pula urlopowa pracownika',
    content: (
      <>
        <p>Każdy pracownik ma trzy liczniki widoczne na jego karcie:</p>
        <ul>
          <li><strong>Przyznane</strong> — roczny limit dni urlopu wypoczynkowego, ustawiany przez Ciebie.</li>
          <li><strong>Użyte</strong> — dni już wykorzystane (liczone automatycznie, bez weekendów).</li>
          <li><strong>Dostępne</strong> — Przyznane minus Użyte.</li>
        </ul>
        <p className="mb-0">
          Dla pracowników dodanych <em>ręcznie</em> możesz zmienić pulę w dowolnym momencie
          (przycisk edycji <FaUserPlus size={12} className="mx-1" />). Dla{' '}
          <em>połączonych kont</em> edytujesz tylko pulę — dane osobowe zarządza sam pracownik.
        </p>
      </>
    ),
  },
  {
    key: 'add-vacation',
    icon: <FaLayerGroup size={20} />,
    title: 'Dodawanie urlopu w kalendarzu',
    content: (
      <>
        <p>
          W zakładce <strong>„Urlopy”</strong> wybierz pracownika z listy po lewej,
          a następnie w kalendarzu:
        </p>
        <ol>
          <li>Kliknij dzień rozpoczęcia urlopu.</li>
          <li>Kliknij dzień zakończenia — pojawi się liczba dni roboczych (weekendy są pomijane).</li>
          <li>Wybierz rodzaj urlopu: <em>wypoczynkowy</em>, <em>okolicznościowy</em> lub <em>bezpłatny</em>.</li>
          <li>Dodaj opcjonalną notatkę i zatwierdź.</li>
        </ol>
        <p className="mb-0">
          <strong>Uwaga:</strong> ta opcja działa tylko dla pracowników dodanych ręcznie.
          Dla połączonych kont kalendarz jest jedynie podglądem zaakceptowanych urlopów —
          urlop zgłasza sam pracownik (patrz niżej).
        </p>
      </>
    ),
  },
  {
    key: 'approve',
    icon: <FaCheckCircle size={20} />,
    title: 'Akceptowanie wniosków urlopowych',
    content: (
      <>
        <p>
          Połączeni pracownicy sami składają wnioski urlopowe ze swojego konta.
          Trafiają one do Ciebie jako <em>oczekujące</em>.
        </p>
        <ul className="mb-0">
          <li>Zobaczysz je w zakładce <strong>„Wnioski urlopowe”</strong> — czerwony licznik przy pozycji w menu pokazuje ich liczbę.</li>
          <li>Dla każdego wniosku wybierz <strong>„Akceptuj”</strong> lub <strong>„Odrzuć”</strong>, opcjonalnie z komentarzem.</li>
          <li>Po akceptacji urlop automatycznie pojawia się w kalendarzu pracownika i pomniejsza jego pulę dostępnych dni.</li>
        </ul>
      </>
    ),
  },
  {
    key: 'slots',
    icon: <FaUsers size={20} />,
    title: 'Sloty pracowników i plany',
    content: (
      <>
        <p>
          Plan darmowy pozwala mieć <strong>1 pracownika</strong> (dodanego ręcznie
          lub połączonego — liczą się razem). Gdy limit zostanie osiągnięty, przyciski
          dodawania/zapraszania zamieniają się w <strong>„Dokup sloty”</strong>.
        </p>
        <ul className="mb-0">
          <li>W zakładce <strong>„Plan subskrypcji”</strong> możesz kupić pakiet S / M / L (10 / 25 / 50 pracowników).</li>
          <li>Płatność jest jednorazowa — plan aktywuje się od razu po opłaceniu przez PayU.</li>
          <li>Jeśli plan wygaśnie, limit automatycznie wraca do 1.</li>
        </ul>
      </>
    ),
  },
];

function EmployerHelp() {
  const [activeKey, setActiveKey] = useState('add-manual');
  const [zoomed, setZoomed] = useState(null);

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.4 },
  };

  return (
    <div style={{ minHeight: '100vh', paddingTop: '2rem', paddingBottom: '4rem' }}>
      {/* Ciemny motyw accordiona — jasny tekst na ciemnym tle */}
      <style>{`
        .help-accordion .accordion-item {
          background-color: #fff !important;
          border: 1px solid var(--pub-line) !important;
        }
        .help-accordion .accordion-button {
          background-color: #fff !important;
          color: var(--pub-text) !important;
          box-shadow: none !important;
        }
        .help-accordion .accordion-button:not(.collapsed) {
          background-color: var(--pub-bg-alt) !important;
          color: var(--pub-navy) !important;
        }
        .help-accordion .accordion-button::after {
          filter: none !important;
        }
        .help-accordion .accordion-button:focus {
          box-shadow: none !important;
          border-color: transparent !important;
        }
        .help-accordion .accordion-body {
          background-color: #fff !important;
          color: var(--pub-muted) !important;
        }
        .help-accordion .accordion-body strong {
          color: var(--pub-text) !important;
        }
        .help-accordion .accordion-body em {
          color: var(--pub-faint) !important;
        }
        .help-accordion .accordion-body li {
          margin-bottom: 4px;
        }
      `}</style>

      <Container fluid className="px-2 px-md-4">

        <motion.div {...fadeInUp} className="mb-4">
          <h1 className="pub-h2 mb-1">Jak korzystać z panelu pracodawcy</h1>
          <p className="pub-muted mb-0">
            Krótki przewodnik po dodawaniu pracowników, urlopach i planach.
          </p>
        </motion.div>

        <motion.div {...fadeInUp}>
          <Card
            className="border-0 shadow-lg mb-4"
            style={{
              borderRadius: '18px',
              background: '#fff',
              border: '1px solid var(--pub-line)',
            }}
          >
            <Card.Body className="p-3 d-flex align-items-center gap-2">
              <FaInfoCircle color="#4dabf7" size={18} />
              <small className="pub-muted">
                Masz dwie ścieżki dodawania pracownika: <strong>ręcznie</strong>{' '}
                (bez konta pracownika) albo przez <strong>połączenie kont</strong>{' '}
                (pracownik ma własne logowanie i sam składa wnioski urlopowe).
              </small>
            </Card.Body>
          </Card>
        </motion.div>

        <Row className="g-4">
          <Col xs={12}>
            <motion.div {...fadeInUp}>
              <Accordion activeKey={activeKey} onSelect={(k) => setActiveKey(k)} className="help-accordion">
                {SECTIONS.map((section, idx) => (
                  <Card
                    key={section.key}
                    className="border-0 shadow-lg mb-3"
                    style={{ borderRadius: '18px', overflow: 'hidden' }}
                  >
                    <Accordion.Item eventKey={section.key} className="border-0">
                      <Accordion.Header>
                        <div className="d-flex align-items-center gap-3">
                          <div
                            className="d-flex align-items-center justify-content-center"
                            style={{
                              width: '38px', height: '38px', borderRadius: '12px',
                              background: 'var(--pub-navy)', color: '#fff', flexShrink: 0,
                            }}
                          >
                            {section.icon}
                          </div>
                          <span className="fw-bold" style={{ color: 'var(--pub-text)' }}>
                            {idx + 1}. {section.title}
                          </span>
                        </div>
                      </Accordion.Header>
                      <Accordion.Body>
                        {section.content}
                        <button
                          type="button"
                          className="help-shot"
                          aria-label={`Powiększ zrzut: ${section.title}`}
                          onClick={() => setZoomed(section)}
                        >
                          <img src={`/pomoc/${section.key}.jpg`} alt={`Zrzut ekranu: ${section.title}`} loading="lazy" />
                        </button>
                      </Accordion.Body>
                    </Accordion.Item>
                  </Card>
                ))}
              </Accordion>
            </motion.div>
          </Col>
        </Row>

        <Modal show={!!zoomed} onHide={() => setZoomed(null)} size="xl" centered>
          <Modal.Header closeButton>
            <Modal.Title as="h2" className="h5">{zoomed?.title}</Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-2">
            {zoomed && (
              <img src={`/pomoc/${zoomed.key}.jpg`} alt={`Zrzut ekranu: ${zoomed.title}`} style={{ width: '100%', height: 'auto' }} />
            )}
          </Modal.Body>
        </Modal>
      </Container>
    </div>
  );
}

export default EmployerHelp;